import React, { useState, useEffect, useRef, useContext } from 'react'
import {
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Row,
  Col,
  Button,
  Input,
  Modal,
  FormGroup,
  Form,
} from 'react-bootstrap'
//import * as Icon from 'react-feather'
import FeatherIcon from 'feather-icons-react'
import { Editor } from '@tinymce/tinymce-react'
import Select from 'react-select'
import Swal from 'sweetalert2'
import httpClient from 'utility/httpClient.util'
import {
  LAST_PAGE_ONLY_ATTRIBUTE,
  applyTextEditorPageRule,
  clearTextEditorPageRules,
  getTextEditorPageRuleBlocks,
  getTextEditorPageRuleSummary,
} from './textEditorPageRule.util'

const unitToPx = (value, fallback) => {
  if (typeof value === 'number') return value

  const match = `${value || ''}`.trim().match(/^([\d.]+)(px|cm|mm|in)?$/)
  if (!match) return fallback

  const amount = Number(match[1])
  const unit = match[2] || 'px'

  if (!Number.isFinite(amount)) return fallback
  if (unit === 'in') return amount * 96
  if (unit === 'cm') return amount * (96 / 2.54)
  if (unit === 'mm') return amount * (96 / 25.4)
  return amount
}

const getPaperGuide = (layout = {}) => {
  const width = unitToPx(layout.width, unitToPx('21cm'))
  const height = unitToPx(layout.height, unitToPx('29.7cm'))

  return {
    width: Math.round(width),
    height: Math.round(height),
    label: `${layout.size || 'A4'} ${layout.orientation || 'portrait'}`,
  }
}

const getEditorHeight = () => {
  if (typeof window === 'undefined') return 600
  return Math.max(360, window.innerHeight - 185)
}

const getDataSourceVariableName = (item = {}) =>
  `${item.dataSourceVariableName || ''}`.trim()

const getDataSourceColumns = (item = {}) =>
  item.fieldVariable?.columns || item.columns || []

const getNamedDataSources = (dataSourceList = []) =>
  (dataSourceList || [])
    .map((item) => ({
      item,
      name: item.dataSourceName || item.name || '',
      variableName: getDataSourceVariableName(item),
      columns: getDataSourceColumns(item).filter(Boolean),
    }))
    .filter((item) => item.variableName)

const escapeHtml = (value = '') =>
  `${value}`
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

const getSelectedTableRow = (editor) =>
  editor?.dom?.getParent(editor.selection.getNode(), 'tr')

const getSelectedTable = (editor) =>
  editor?.dom?.getParent(editor.selection.getNode(), 'table')

const getTableRowFromNode = (editor, node) => editor?.dom?.getParent(node, 'tr')

const getTableFromNode = (editor, node) => {
  if (node?.nodeName === 'TABLE') return node

  return editor?.dom?.getParent(node, 'table') || getSelectedTable(editor)
}

const isEmptyTableCell = (cell) => {
  const html = `${cell?.innerHTML || ''}`
    .replace(/<br[^>]*>/gi, '')
    .replace(/&nbsp;/gi, '')
    .trim()

  return !html && !`${cell?.textContent || ''}`.trim()
}

const getDatasourceVariableToken = (dataSource, column) =>
  '{' + dataSource.variableName + '.' + column + '}'

const reportVariableTokenRegExp = /\{([A-Za-z_][A-Za-z0-9_]*)\.([A-Za-z_][A-Za-z0-9_.-]*)(?:\|([A-Za-z_][A-Za-z0-9_-]*)(?::([^}]*))?)?\}/g
const PAGE_RULE_VISIBILITY_OPTIONS = [
  { value: 'all', label: 'ทุกหน้า' },
  { value: 'first', label: 'หน้าแรกเท่านั้น' },
  { value: 'last', label: 'หน้าสุดท้ายเท่านั้น (เช่น หน้า 2)' },
]

const parseReportVariableToken = (token = '') => {
  const match = String(token || '').trim().match(
    /^\{([A-Za-z_][A-Za-z0-9_]*)\.([A-Za-z_][A-Za-z0-9_.-]*)(?:\|([A-Za-z_][A-Za-z0-9_-]*)(?::([^}]*))?)?\}$/,
  )

  if (!match) return null

  return {
    token: match[0],
    variableName: match[1],
    fieldPath: match[2],
    formatName: match[3] || '',
    formatValue: match[4] || '',
  }
}

const buildReportVariableToken = (token, formatName = '', formatValue = '') => {
  const nextFormatName = String(formatName || '').trim()
  const nextFormatValue = String(formatValue || '').trim()
  const formatText = nextFormatName
    ? '|' + nextFormatName + (nextFormatValue ? ':' + nextFormatValue : '')
    : ''

  return '{' + token.variableName + '.' + token.fieldPath + formatText + '}'
}

const hasReportVariableToken = (text = '') => {
  reportVariableTokenRegExp.lastIndex = 0
  return reportVariableTokenRegExp.test(text || '')
}

const findFirstTextNodeWithReportToken = (root) => {
  if (!root) return null
  if (root.nodeType === 3 && hasReportVariableToken(root.nodeValue)) {
    return root
  }

  const documentRef = root.ownerDocument || document
  const walker = documentRef.createTreeWalker(root, 4, null)
  let textNode = walker.nextNode()

  while (textNode) {
    if (hasReportVariableToken(textNode.nodeValue)) return textNode
    textNode = walker.nextNode()
  }

  return null
}

const getSelectionTextNode = (editor, range) => {
  const container = range?.startContainer
  if (!container) return null
  if (container.nodeType === 3) return container

  const childNodes = container.childNodes || []
  const targetNode =
    childNodes[range.startOffset] || childNodes[range.startOffset - 1]

  return (
    findFirstTextNodeWithReportToken(targetNode) ||
    findFirstTextNodeWithReportToken(editor.selection.getNode())
  )
}

const findReportVariableTokenInTextNode = (textNode, offset = null) => {
  if (!textNode) return null

  const text = textNode.nodeValue || ''
  reportVariableTokenRegExp.lastIndex = 0
  let match = reportVariableTokenRegExp.exec(text)
  let fallback = null

  while (match) {
    const start = match.index
    const end = start + match[0].length
    const result = {
      textNode,
      start,
      end,
      token: parseReportVariableToken(match[0]),
    }

    if (offset === null || (offset >= start && offset <= end)) {
      return result
    }

    fallback = fallback || result
    match = reportVariableTokenRegExp.exec(text)
  }

  return fallback
}

const getReportVariableTokenAtSelection = (editor) => {
  if (!editor?.selection) return null

  const range = editor.selection.getRng()
  const selectedText = editor.selection.getContent({ format: 'text' })
  const selectedToken = !range.collapsed
    ? parseReportVariableToken(selectedText)
    : null

  if (selectedToken) {
    return {
      replaceSelection: true,
      token: selectedToken,
    }
  }

  const textNode = getSelectionTextNode(editor, range)
  const offset = textNode === range.startContainer ? range.startOffset : null

  return findReportVariableTokenInTextNode(textNode, offset)
}

const setEditorSelectionFromPoint = (editor, event) => {
  const documentRef = editor.getDoc()
  let range = null

  if (documentRef.caretRangeFromPoint) {
    range = documentRef.caretRangeFromPoint(event.clientX, event.clientY)
  } else if (documentRef.caretPositionFromPoint) {
    const position = documentRef.caretPositionFromPoint(
      event.clientX,
      event.clientY,
    )

    if (position) {
      range = documentRef.createRange()
      range.setStart(position.offsetNode, position.offset)
      range.collapse(true)
    }
  }

  if (range) {
    editor.selection.setRng(range)
    editor.nodeChanged()
  }
}

const removeEditorStyleProperties = (styleText = '', propertyNames = []) => {
  const normalizedNames = propertyNames.map((name) => name.toLowerCase())

  return `${styleText || ''}`
    .split(';')
    .map((item) => item.trim())
    .filter((item) => {
      const propertyName = item.split(':')[0]?.trim().toLowerCase()
      return item && !normalizedNames.includes(propertyName)
    })
    .join('; ')
}

const setEditorStyleAttribute = (editor, node, styleText = '') => {
  editor.dom.setAttrib(node, 'style', styleText || null)
}

const appendEditorStyleProperties = (styleText = '', properties = {}) => {
  const propertyNames = Object.keys(properties)
  const currentStyle = removeEditorStyleProperties(styleText, propertyNames)
  const nextStyle = propertyNames
    .map((propertyName) => `${propertyName}: ${properties[propertyName]}`)
    .join('; ')

  return [currentStyle, nextStyle].filter(Boolean).join('; ')
}

const getTableRows = (table) => Array.from(table?.rows || [])

const getRenderedRowHeight = (row) => {
  const rect = row?.getBoundingClientRect?.()
  if (rect?.height > 0) return rect.height

  const inlineHeight = parseFloat(row?.style?.height)
  return Number.isFinite(inlineHeight) && inlineHeight > 0 ? inlineHeight : null
}

const getRowHeightText = (height) => {
  const parsed = Number(height)
  if (!Number.isFinite(parsed) || parsed <= 0) return null

  return Math.round(parsed) + 'px'
}

const getInlineHeightText = (height = '') => {
  const match = String(height || '')
    .trim()
    .match(/^([0-9.]+)(px|cm|mm|in|pt|%)?$/i)

  if (!match) return null

  const parsed = Number(match[1])
  if (!Number.isFinite(parsed) || parsed <= 0) return null

  return parsed + (match[2] || 'px')
}

const getEditorStyleProperty = (styleText = '', propertyName = '') => {
  const normalizedName = propertyName.toLowerCase()
  const styleItem = String(styleText || '')
    .split(';')
    .map((item) => item.trim())
    .find((item) => item.split(':')[0]?.trim().toLowerCase() === normalizedName)

  return styleItem ? styleItem.split(':').slice(1).join(':').trim() : ''
}

const getTableColumnCount = (table) =>
  getTableRows(table).reduce((count, row) => {
    const rowColumnCount = Array.from(row?.cells || []).reduce(
      (total, cell) => total + (parseInt(cell.getAttribute('colspan'), 10) || 1),
      0,
    )

    return Math.max(count, rowColumnCount)
  }, 0)

const getStyleWidthText = (styleText = '') =>
  getEditorStyleProperty(styleText, 'width')

const getNodeWidthText = (node) =>
  getStyleWidthText(node?.getAttribute?.('style')) ||
  `${node?.getAttribute?.('width') || ''}`.trim()

const normalizeWidthText = (width) => {
  const widthText = `${width || ''}`.trim()

  if (!widthText) return null
  if (/^[0-9.]+$/.test(widthText)) return widthText + 'px'
  if (/^[0-9.]+(px|cm|mm|in|pt|%)$/i.test(widthText)) return widthText

  return null
}

const getWidthAmount = (width) => {
  const match = `${width || ''}`.trim().match(/^([0-9.]+)(px|%)?$/i)
  if (!match) return null

  const amount = Number(match[1])
  return Number.isFinite(amount) && amount > 0 ? amount : null
}

const formatPercentWidth = (width) => {
  const rounded = Math.round(width * 10000) / 10000
  return `${rounded}`.replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1') + '%'
}

const getPercentColumnWidths = (columnWidths = []) => {
  const amounts = columnWidths.map(getWidthAmount)

  if (!amounts.length || amounts.some((amount) => !amount)) return columnWidths

  const total = amounts.reduce((sum, amount) => sum + amount, 0)
  if (!total) return columnWidths

  return amounts.map((amount) => formatPercentWidth((amount / total) * 100))
}

const getRenderedWidthText = (node) => {
  const rect = node?.getBoundingClientRect?.()
  if (!rect?.width) return null

  return Math.round(rect.width) + 'px'
}

const getTableColgroup = (table) =>
  Array.from(table?.children || []).find((child) => child.nodeName === 'COLGROUP')

const getColgroupColumns = (colgroup) =>
  Array.from(colgroup?.children || []).filter((child) => child.nodeName === 'COL')

const getColgroupColumnWidths = (table) => {
  const columns = getColgroupColumns(getTableColgroup(table))

  return columns.map((column) =>
    getRenderedWidthText(column) ||
    normalizeWidthText(getNodeWidthText(column)),
  )
}

const getCellColumnWidths = (table, columnCount) => {
  const widths = Array(columnCount).fill(null)
  const rowspans = []

  getTableRows(table).forEach((row) => {
    let columnIndex = 0

    Array.from(row.cells || []).forEach((cell) => {
      while (rowspans[columnIndex] > 0) {
        rowspans[columnIndex] -= 1
        columnIndex += 1
      }

      const colspan = parseInt(cell.getAttribute('colspan'), 10) || 1
      const rowspan = parseInt(cell.getAttribute('rowspan'), 10) || 1

      if (colspan === 1 && !widths[columnIndex]) {
        widths[columnIndex] =
          getRenderedWidthText(cell) ||
          normalizeWidthText(getNodeWidthText(cell))
      }

      if (rowspan > 1) {
        for (let index = 0; index < colspan; index += 1) {
          rowspans[columnIndex + index] = rowspan - 1
        }
      }

      columnIndex += colspan
    })

    for (let index = columnIndex; index < rowspans.length; index += 1) {
      if (rowspans[index] > 0) rowspans[index] -= 1
    }
  })

  return widths
}

const getTableColumnWidths = (table) => {
  const columnCount = getTableColumnCount(table)
  if (!columnCount) return []

  const colgroupWidths = getColgroupColumnWidths(table)
  const cellWidths = getCellColumnWidths(table, columnCount)

  const columnWidths = []

  for (let index = 0; index < columnCount; index += 1) {
    columnWidths.push(
      normalizeWidthText(cellWidths[index]) ||
        normalizeWidthText(colgroupWidths[index]),
    )
  }

  return columnWidths
}

const ensureTableColgroup = (table, columnCount) => {
  if (!columnCount) return null

  let colgroup = getTableColgroup(table)
  if (!colgroup) {
    const caption = Array.from(table.children || []).find(
      (child) => child.nodeName === 'CAPTION',
    )

    colgroup = table.ownerDocument.createElement('colgroup')
    table.insertBefore(colgroup, caption?.nextSibling || table.firstChild)
  }

  const columns = getColgroupColumns(colgroup)
  while (columns.length < columnCount) {
    const column = table.ownerDocument.createElement('col')
    colgroup.appendChild(column)
    columns.push(column)
  }

  while (columns.length > columnCount) {
    columns.pop().remove()
  }

  return colgroup
}

const persistTableColumnWidths = (editor, table) => {
  const columnWidths = getPercentColumnWidths(getTableColumnWidths(table))
  if (!columnWidths.length || columnWidths.every((width) => !width)) return null

  const colgroup = ensureTableColgroup(table, columnWidths.length)
  const columns = getColgroupColumns(colgroup)

  columns.forEach((column, index) => {
    const width = columnWidths[index]
    if (!width) return

    setEditorStyleAttribute(
      editor,
      column,
      appendEditorStyleProperties(editor.dom.getAttrib(column, 'style'), {
        width,
      }),
    )
    editor.dom.setAttrib(column, 'width', width)
  })

  return columnWidths
}

const persistAllTableColumnWidths = (editor) => {
  const tables = Array.from(editor?.getBody?.()?.querySelectorAll('table') || [])
  tables.forEach((table) => persistTableColumnWidths(editor, table))
}

const getNodeInlineHeightText = (node) =>
  getInlineHeightText(getEditorStyleProperty(node?.getAttribute?.('style'), 'height'))

const getRowSaveHeightText = (row) => {
  const rowHeight =
    getInlineHeightText(row?.getAttribute?.('data-row-height')) ||
    getNodeInlineHeightText(row)

  if (rowHeight) return rowHeight

  const cells = Array.from(row?.children || []).filter((cell) =>
    ['TD', 'TH'].includes(cell.nodeName),
  )

  const cell = cells.find((cell) => getNodeInlineHeightText(cell))
  return getNodeInlineHeightText(cell)
}

const setTableRowHeight = (editor, row, heightText) => {
  editor.dom.setAttrib(row, 'data-row-height', heightText)
  setEditorStyleAttribute(
    editor,
    row,
    appendEditorStyleProperties(editor.dom.getAttrib(row, 'style'), {
      height: heightText,
    }),
  )

  Array.from(row.children || []).forEach((cell) => {
    if (!['TD', 'TH'].includes(cell.nodeName)) return

    setEditorStyleAttribute(
      editor,
      cell,
      appendEditorStyleProperties(editor.dom.getAttrib(cell, 'style'), {
        height: heightText,
      }),
    )
  })
}

const applyTableRowHeightFromFirstRow = (editor, table) => {
  const rows = getTableRows(table)
  const heightText = getRowHeightText(getRenderedRowHeight(rows[0]))

  if (!rows.length || !heightText) return null

  rows.forEach((row) => setTableRowHeight(editor, row, heightText))

  return {
    heightText,
    rowCount: rows.length,
  }
}

const persistTableRowHeight = (editor, row) => {
  const heightText = getRowSaveHeightText(row) || getRowHeightText(
    getRenderedRowHeight(row),
  )

  if (!heightText) return null

  setTableRowHeight(editor, row, heightText)
  return heightText
}

const persistMarkedTableRowHeights = (editor) => {
  const rows = Array.from(
    editor?.getBody?.()?.querySelectorAll('tr[data-row-height]') || [],
  )

  rows.forEach((row) => persistTableRowHeight(editor, row))
}

const persistResizedTableRow = (editor, target) => {
  const row = getTableRowFromNode(editor, target) || getSelectedTableRow(editor)
  return row ? persistTableRowHeight(editor, row) : null
}

const persistResizedTable = (editor, target) => {
  const table = getTableFromNode(editor, target)

  persistResizedTableRow(editor, target)
  return table ? persistTableColumnWidths(editor, table) : null
}

const resetRepeatRowHeight = (editor, row) => {
  editor.dom.setAttrib(row, 'data-row-height', null)
  setEditorStyleAttribute(
    editor,
    row,
    removeEditorStyleProperties(editor.dom.getAttrib(row, 'style'), [
      'height',
      'min-height',
    ]),
  )

  Array.from(row.children || []).forEach((cell) => {
    if (!['TD', 'TH'].includes(cell.nodeName)) return

    setEditorStyleAttribute(
      editor,
      cell,
      removeEditorStyleProperties(editor.dom.getAttrib(cell, 'style'), [
        'height',
        'min-height',
      ]),
    )
  })
}

const setRepeatBodyLinesHidden = (editor, row, isHidden) => {
  editor.dom.setAttrib(row, 'data-repeat-body-lines', isHidden ? 'hidden' : null)

  Array.from(row.children || []).forEach((cell) => {
    if (!['TD', 'TH'].includes(cell.nodeName)) return

    const cellStyle = editor.dom.getAttrib(cell, 'style')
    const nextStyle = isHidden
      ? appendEditorStyleProperties(cellStyle, {
          'border-top': '0',
          'border-bottom': '0',
        })
      : removeEditorStyleProperties(cellStyle, ['border-top', 'border-bottom'])

    setEditorStyleAttribute(editor, cell, nextStyle)
  })
}

const setLastPageOnly = (editor, row, isLastPageOnly) => {
  editor.dom.setAttrib(
    row,
    LAST_PAGE_ONLY_ATTRIBUTE,
    isLastPageOnly ? 'true' : null,
  )

  if (isLastPageOnly) {
    editor.dom.addClass(row, 'report-last-page-only')
    return
  }

  editor.dom.removeClass(row, 'report-last-page-only')
}

const REPORT_CUSTOM_STYLE_ID = 'classic-report-custom-style'

const applyReportCustomStyle = (editor, styleData = '') => {
  const editorDocument = editor?.getDoc?.()
  if (!editorDocument?.head) return

  let styleElement = editorDocument.getElementById(REPORT_CUSTOM_STYLE_ID)
  if (!styleElement) {
    styleElement = editorDocument.createElement('style')
    styleElement.id = REPORT_CUSTOM_STYLE_ID
    editorDocument.head.appendChild(styleElement)
  }

  styleElement.textContent = styleData || ''
}

const TinyEditorModal = ({
  showModal,
  setShowModal,
  sourceCode,
  styleData = '',
  onSave,
  layout,
  dataSourceList = [],
}) => {
  const editorRef = useRef(null)
  const wordInputRef = useRef(null)
  const dataSourceRef = useRef([])
  const [isImportingWord, setIsImportingWord] = useState(false)
  const [fitInfo, setFitInfo] = useState(null)
  const [editorHeight, setEditorHeight] = useState(getEditorHeight())
  const [activeEditorTab, setActiveEditorTab] = useState('content')
  const [styleDataDraft, setStyleDataDraft] = useState(styleData || '')
  const [pageRuleBlocks, setPageRuleBlocks] = useState([])
  const [pageRuleStartIndex, setPageRuleStartIndex] = useState(null)
  const [pageRuleEndIndex, setPageRuleEndIndex] = useState(null)
  const [pageRuleVisibleOn, setPageRuleVisibleOn] = useState('first')
  const [collapsePageRuleSpace, setCollapsePageRuleSpace] = useState(true)
  const paperGuide = getPaperGuide(layout)
  const dataSources = getNamedDataSources(dataSourceList)
  dataSourceRef.current = dataSources

  const updateFitInfo = () => {
    const editor = editorRef.current
    const body = editor?.getBody?.()
    if (!body) return

    const bodyRect = body.getBoundingClientRect()
    const contentSize = Array.from(body.children).reduce(
      (size, child) => {
        const rect = child.getBoundingClientRect()
        return {
          width: Math.max(size.width, rect.right - bodyRect.left),
          height: Math.max(size.height, rect.bottom - bodyRect.top),
        }
      },
      { width: body.scrollWidth, height: body.scrollHeight },
    )

    setFitInfo({
      width: Math.ceil(contentSize.width),
      height: Math.ceil(contentSize.height),
      isOverflowWidth: contentSize.width > paperGuide.width + 2,
      isOverflowHeight: contentSize.height > paperGuide.height + 2,
    })
  }

  const toggleModal = () => {
    setShowModal(!showModal)
  }

  const handleSave = () => {
    if (editorRef.current) {
      persistMarkedTableRowHeights(editorRef.current)
      persistAllTableColumnWidths(editorRef.current)
      onSave(editorRef.current.getContent(), styleDataDraft)
      toggleModal()
    }
  }

  const handleImportWordClick = () => {
    wordInputRef.current?.click()
  }

  const notifyEditor = (editor, type, text) => {
    editor.notificationManager.open({
      text,
      type,
      timeout: 2500,
    })
  }

  const refreshPageRuleBlocks = (preferEditorSelection = false) => {
    const editor = editorRef.current
    const body = editor?.getBody?.()
    if (!body) return

    const nextBlocks = getTextEditorPageRuleBlocks(body)
    setPageRuleBlocks(nextBlocks)

    let selectedIndex = null
    if (preferEditorSelection) {
      const selectedNode = editor.selection?.getNode?.()
      const selectedParagraph =
        selectedNode?.nodeName === 'P'
          ? selectedNode
          : editor.dom?.getParent?.(selectedNode, 'p')
      selectedIndex = Array.from(body.querySelectorAll('p')).indexOf(
        selectedParagraph,
      )
      if (selectedIndex < 0) selectedIndex = null
    }

    setPageRuleStartIndex((currentIndex) =>
      selectedIndex !== null
        ? selectedIndex
        : nextBlocks[currentIndex]
        ? currentIndex
        : null,
    )
    setPageRuleEndIndex((currentIndex) =>
      selectedIndex !== null
        ? selectedIndex
        : nextBlocks[currentIndex]
        ? currentIndex
        : null,
    )
  }

  const openPageRuleTab = () => {
    refreshPageRuleBlocks()
    setActiveEditorTab('page-rule')
  }

  const handleApplyPageRule = () => {
    const editor = editorRef.current
    if (
      !editor?.getBody?.() ||
      pageRuleStartIndex === null ||
      pageRuleEndIndex === null
    ) {
      Swal.fire('การแบ่งหน้า', 'กรุณาเลือกช่วงย่อหน้าที่ต้องการ', 'warning')
      return
    }

    let updatedCount = 0
    editor.undoManager.transact(() => {
      updatedCount = applyTextEditorPageRule(editor.getBody(), {
        startIndex: pageRuleStartIndex,
        endIndex: pageRuleEndIndex,
        visibleOn: pageRuleVisibleOn,
        collapseWhenHidden: collapsePageRuleSpace,
      })
    })
    editor.setDirty(true)
    editor.nodeChanged()
    refreshPageRuleBlocks()
    notifyEditor(
      editor,
      'success',
      `ตั้งค่าการแบ่งหน้า ${updatedCount} ย่อหน้าแล้ว`,
    )
  }

  const handleClearPageRules = () => {
    const editor = editorRef.current
    if (!editor?.getBody?.()) return

    editor.undoManager.transact(() => {
      clearTextEditorPageRules(editor.getBody())
    })
    editor.setDirty(true)
    editor.nodeChanged()
    refreshPageRuleBlocks()
    notifyEditor(editor, 'success', 'ล้างกฎการแบ่งหน้าทั้งหมดแล้ว')
  }

  const handleEditPageRule = (rule) => {
    setPageRuleStartIndex(rule.startIndex)
    setPageRuleEndIndex(rule.endIndex)
    setPageRuleVisibleOn(rule.visibleOn)
    setCollapsePageRuleSpace(rule.collapseWhenHidden)
  }

  const handleRemovePageRule = (rule) => {
    const editor = editorRef.current
    if (!editor?.getBody?.()) return

    editor.undoManager.transact(() => {
      applyTextEditorPageRule(editor.getBody(), {
        startIndex: rule.startIndex,
        endIndex: rule.endIndex,
        visibleOn: 'all',
      })
    })
    editor.setDirty(true)
    editor.nodeChanged()
    refreshPageRuleBlocks()
    notifyEditor(editor, 'success', 'ลบกฎการแบ่งหน้าช่วงนี้แล้ว')
  }

  const getCurrentDataSources = () => dataSourceRef.current || []

  const canApplyDatasourceRepeat = (editor) =>
    !!getSelectedTableRow(editor) && getCurrentDataSources().length > 0

  const canClearDatasourceRepeat = (editor) => {
    const row = getSelectedTableRow(editor)
    return !!row?.getAttribute?.('data-repeat')
  }

  const canSetRepeatBodyLines = (editor) => {
    const row = getSelectedTableRow(editor)
    return !!row?.getAttribute?.('data-repeat')
  }

  const canSetLastPageOnly = (editor) => !!getSelectedTableRow(editor)

  const canEqualizeTableRowHeight = (editor) => !!getSelectedTable(editor)

  const isRepeatBodyLinesHidden = (editor) => {
    const row = getSelectedTableRow(editor)
    return row?.getAttribute?.('data-repeat-body-lines') === 'hidden'
  }

  const isLastPageOnly = (editor) => {
    const row = getSelectedTableRow(editor)
    return row?.getAttribute?.(LAST_PAGE_ONLY_ATTRIBUTE) === 'true'
  }

  const bindRepeatEnableState = (editor, api) => {
    const updateEnabled = () => {
      api.setEnabled?.(canApplyDatasourceRepeat(editor))
    }

    editor.on('NodeChange', updateEnabled)
    updateEnabled()

    return () => {
      editor.off('NodeChange', updateEnabled)
    }
  }

  const bindClearRepeatEnableState = (editor, api) => {
    const updateEnabled = () => {
      api.setEnabled?.(canClearDatasourceRepeat(editor))
    }

    editor.on('NodeChange', updateEnabled)
    updateEnabled()

    return () => {
      editor.off('NodeChange', updateEnabled)
    }
  }

  const bindRepeatBodyLinesState = (editor, api) => {
    const updateState = () => {
      api.setEnabled?.(canSetRepeatBodyLines(editor))
      api.setActive?.(isRepeatBodyLinesHidden(editor))
    }

    editor.on('NodeChange', updateState)
    updateState()

    return () => {
      editor.off('NodeChange', updateState)
    }
  }

  const bindLastPageOnlyState = (editor, api) => {
    const updateState = () => {
      api.setEnabled?.(canSetLastPageOnly(editor))
      api.setActive?.(isLastPageOnly(editor))
    }

    editor.on('NodeChange', updateState)
    updateState()

    return () => {
      editor.off('NodeChange', updateState)
    }
  }

  const bindTableRowHeightEnableState = (editor, api) => {
    const updateEnabled = () => {
      api.setEnabled?.(canEqualizeTableRowHeight(editor))
    }

    editor.on('NodeChange', updateEnabled)
    updateEnabled()

    return () => {
      editor.off('NodeChange', updateEnabled)
    }
  }

  const equalizeTableRowHeightFromFirstRow = (editor) => {
    const table = getSelectedTable(editor)
    if (!table) {
      notifyEditor(editor, 'warning', 'กรุณาคลิกในตารางก่อนปรับความสูงแถว')
      return
    }

    const result = applyTableRowHeightFromFirstRow(editor, table)
    if (!result) {
      notifyEditor(editor, 'warning', 'ไม่พบความสูงของแถวแรกในตาราง')
      return
    }

    editor.nodeChanged()
    editor.fire('Change')
    window.setTimeout(updateFitInfo, 0)
    notifyEditor(
      editor,
      'success',
      'ปรับความสูง ' + result.rowCount + ' แถวเป็น ' + result.heightText + ' แล้ว',
    )
  }

  const toggleRepeatBodyLines = (editor) => {
    const row = getSelectedTableRow(editor)
    if (!row?.getAttribute?.('data-repeat')) {
      notifyEditor(editor, 'warning', 'กรุณาคลิกในแถว repeat ก่อนปรับเส้น body')
      return
    }

    const isHidden = row.getAttribute('data-repeat-body-lines') === 'hidden'
    setRepeatBodyLinesHidden(editor, row, !isHidden)
    editor.nodeChanged()
    editor.fire('Change')
    window.setTimeout(updateFitInfo, 0)
    notifyEditor(
      editor,
      'success',
      !isHidden ? 'ซ่อนเส้นคั่นรายการแล้ว' : 'แสดงเส้นคั่นรายการแล้ว',
    )
  }

  const toggleLastPageOnly = (editor) => {
    const row = getSelectedTableRow(editor)
    if (!row) {
      notifyEditor(editor, 'warning', 'กรุณาคลิกในแถวตารางก่อนตั้งค่าหน้าสุดท้าย')
      return
    }

    const isEnabled = row.getAttribute(LAST_PAGE_ONLY_ATTRIBUTE) === 'true'
    setLastPageOnly(editor, row, !isEnabled)
    editor.nodeChanged()
    editor.fire('Change')
    window.setTimeout(updateFitInfo, 0)
    notifyEditor(
      editor,
      'success',
      !isEnabled
        ? 'ตั้งค่าให้แถวนี้แสดงเฉพาะหน้าสุดท้ายแล้ว'
        : 'ยกเลิกการแสดงเฉพาะหน้าสุดท้ายแล้ว',
    )
  }

  const canFormatReportVariable = (editor) =>
    !!getReportVariableTokenAtSelection(editor)

  const bindVariableFormatEnableState = (editor, api) => {
    const updateEnabled = () => {
      api.setEnabled?.(canFormatReportVariable(editor))
    }

    editor.on('NodeChange KeyUp MouseUp', updateEnabled)
    updateEnabled()

    return () => {
      editor.off('NodeChange KeyUp MouseUp', updateEnabled)
    }
  }

  const applyReportVariableFormat = (editor, formatName = '', formatValue = '') => {
    const target = getReportVariableTokenAtSelection(editor)

    if (!target) {
      notifyEditor(editor, 'warning', 'กรุณาคลิกบนตัวแปรก่อนเลือก format')
      return
    }

    const nextToken = buildReportVariableToken(
      target.token,
      formatName,
      formatValue,
    )

    if (target.replaceSelection) {
      editor.selection.setContent(escapeHtml(nextToken))
    } else {
      const currentText = target.textNode.nodeValue || ''
      target.textNode.nodeValue =
        currentText.slice(0, target.start) +
        nextToken +
        currentText.slice(target.end)

      const range = target.textNode.ownerDocument.createRange()
      range.setStart(target.textNode, target.start)
      range.setEnd(target.textNode, target.start + nextToken.length)
      editor.selection.setRng(range)
    }

    editor.nodeChanged()
    editor.fire('Change')
    window.setTimeout(updateFitInfo, 0)
    notifyEditor(editor, 'success', 'ปรับ format ตัวแปรแล้ว')
  }

  const applyDatasourceRepeat = (editor, dataSource) => {
    const row = getSelectedTableRow(editor)
    if (!row) {
      notifyEditor(editor, 'warning', 'กรุณาคลิกในแถวตารางก่อนเพิ่ม repeat')
      return
    }

    const variableName = dataSource.variableName
    editor.dom.setAttrib(row, 'data-repeat', variableName)
    editor.dom.setAttrib(row, 'data-repeat-source', dataSource.name)
    editor.dom.addClass(row, 'report-repeat-row')
    resetRepeatRowHeight(editor, row)

    const cells = Array.from(row.children).filter((cell) =>
      ['TD', 'TH'].includes(cell.nodeName),
    )

    dataSource.columns.forEach((column, index) => {
      const cell = cells[index]
      if (cell && isEmptyTableCell(cell)) {
        cell.innerHTML = escapeHtml(
          getDatasourceVariableToken(dataSource, column),
        )
      }
    })

    editor.selection.select(row)
    editor.nodeChanged()
    editor.fire('Change')
    window.setTimeout(updateFitInfo, 0)
    notifyEditor(editor, 'success', `เพิ่ม repeat ${variableName} แล้ว`)
  }

  const clearDatasourceRepeat = (editor) => {
    const row = getSelectedTableRow(editor)
    if (!row) {
      notifyEditor(editor, 'warning', 'กรุณาคลิกในแถวตารางก่อนลบ repeat')
      return
    }

    editor.dom.setAttrib(row, 'data-repeat', null)
    editor.dom.setAttrib(row, 'data-repeat-source', null)
    setRepeatBodyLinesHidden(editor, row, false)
    editor.dom.removeClass(row, 'report-repeat-row')
    editor.nodeChanged()
    editor.fire('Change')
    window.setTimeout(updateFitInfo, 0)
    notifyEditor(editor, 'success', 'ลบ repeat จากแถวนี้แล้ว')
  }

  const openRepeatDatasourceDialog = (editor) => {
    const dataSources = getCurrentDataSources()
    const row = getSelectedTableRow(editor)

    if (!dataSources.length) {
      notifyEditor(editor, 'warning', 'ยังไม่มี Data Source ที่ตั้งชื่อตัวแปร')
      return
    }

    if (!row) {
      notifyEditor(editor, 'warning', 'กรุณาคลิกในแถวตารางก่อนเพิ่ม repeat')
      return
    }

    editor.windowManager.open({
      title: 'Add Datasource Repeat',
      body: {
        type: 'panel',
        items: [
          {
            type: 'selectbox',
            name: 'variableName',
            label: 'Data Source',
            items: dataSources.map((item) => ({
              text: item.name
                ? `${item.variableName} - ${item.name}`
                : item.variableName,
              value: item.variableName,
            })),
          },
        ],
      },
      buttons: [
        {
          type: 'cancel',
          text: 'Cancel',
        },
        {
          type: 'submit',
          text: 'Apply',
          primary: true,
        },
      ],
      initialData: {
        variableName: dataSources[0]?.variableName,
      },
      onSubmit: (api) => {
        const { variableName } = api.getData()
        const dataSource = dataSources.find(
          (item) => item.variableName === variableName,
        )

        if (dataSource) {
          applyDatasourceRepeat(editor, dataSource)
        }

        api.close()
      },
    })
  }

  const getEmptyDatasourceMenuItems = () => [
    {
      type: 'menuitem',
      text: 'ยังไม่มี Data Source ที่ตั้งชื่อตัวแปร',
      disabled: true,
      onAction: () => {},
    },
  ]

  const insertDatasourceVariable = (editor, dataSource, column) => {
    editor.insertContent(
      escapeHtml(getDatasourceVariableToken(dataSource, column)),
    )
    editor.fire('Change')
    window.setTimeout(updateFitInfo, 0)
  }

  const getVariableMenuItems = (editor) => {
    const dataSources = getCurrentDataSources()

    if (!dataSources.length) return getEmptyDatasourceMenuItems()

    return dataSources.map((dataSource) => ({
      type: 'nestedmenuitem',
      text: dataSource.name
        ? `${dataSource.variableName} - ${dataSource.name}`
        : dataSource.variableName,
      getSubmenuItems: () => {
        if (!dataSource.columns.length) {
          return [
            {
              type: 'menuitem',
              text: 'ไม่มี column',
              disabled: true,
              onAction: () => {},
            },
          ]
        }

        return dataSource.columns.map((column) => ({
          type: 'menuitem',
          text: `${dataSource.variableName}.${column}`,
          onAction: () => insertDatasourceVariable(editor, dataSource, column),
        }))
      },
    }))
  }

  const getRepeatDatasourceMenuItems = (editor) => {
    const dataSources = getCurrentDataSources()

    if (!dataSources.length) return getEmptyDatasourceMenuItems()

    return dataSources.map((dataSource) => ({
      type: 'menuitem',
      text: dataSource.name
        ? `${dataSource.variableName} - ${dataSource.name}`
        : dataSource.variableName,
      onAction: () => applyDatasourceRepeat(editor, dataSource),
    }))
  }

  const handleImportWord = async (event) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return

    if (!file.name.toLowerCase().endsWith('.docx')) {
      Swal.fire('Import Word', 'รองรับเฉพาะไฟล์ .docx', 'warning')
      return
    }

    const formData = new FormData()
    formData.append('file', file)
    setIsImportingWord(true)
    try {
      const response = (
        await httpClient.post('/report/report-webview/docx-to-html', formData)
      ).data
      const html = response?.data?.html || ''
      editorRef.current?.setContent(html)
    } catch (error) {
      Swal.fire(
        'Import Word',
        error?.response?.data?.message || 'ไม่สามารถแปลงไฟล์ Word ได้',
        'error',
      )
    } finally {
      setIsImportingWord(false)
      window.setTimeout(updateFitInfo, 0)
    }
  }

  useEffect(() => {
    if (showModal) {
      setActiveEditorTab('content')
      setStyleDataDraft(styleData || '')
      setPageRuleBlocks([])
      setPageRuleStartIndex(null)
      setPageRuleEndIndex(null)
      setPageRuleVisibleOn('first')
      setCollapsePageRuleSpace(true)
    }
  }, [showModal, styleData])

  useEffect(() => {
    if (showModal) {
      window.setTimeout(updateFitInfo, 0)
    }
  }, [showModal, layout])

  useEffect(() => {
    if (!showModal) return

    applyReportCustomStyle(editorRef.current, styleDataDraft)
    window.setTimeout(updateFitInfo, 0)
  }, [showModal, styleDataDraft])

  useEffect(() => {
    const resizeEditor = () => {
      const nextHeight = getEditorHeight()
      setEditorHeight(nextHeight)
      editorRef.current?.theme?.resizeTo?.(null, nextHeight)
      window.setTimeout(updateFitInfo, 0)
    }

    if (showModal) {
      resizeEditor()
      window.addEventListener('resize', resizeEditor)
    }

    return () => {
      window.removeEventListener('resize', resizeEditor)
    }
  }, [showModal])

  const editorContentStyle = `
    @import url('/his/fonts/sarabun.css');
    @import url('/his/fonts/prompt.css');
    @import url('/his/fonts/angsana-new.css');
    html {
      background: #eef1f5;
      overflow: auto;
    }
    body {
      width: ${paperGuide.width}px;
      min-height: ${paperGuide.height}px;
      margin: 16px auto;
      padding: 0;
      background: #fff;
      box-sizing: border-box;
      outline: 2px dashed #8b8f97;
      overflow: visible;
      font-family: Sarabun, sans-serif;
      font-size: 14px;
      line-height: 1;
    }
    p {
      line-height: 1.4rem;
      margin-top: 0.3rem;
      margin-bottom: 0.3rem;
    }
    tr[data-repeat]:not([data-row-height]) {
      height: auto !important;
    }
    tr[data-repeat]:not([data-row-height]) > td,
    tr[data-repeat]:not([data-row-height]) > th {
      height: auto !important;
      box-shadow: inset 0 0 0 2px #4099ff;
    }
    tr[data-repeat][data-row-height] > td,
    tr[data-repeat][data-row-height] > th {
      box-shadow: inset 0 0 0 2px #4099ff;
    }
    tr[data-repeat-body-lines="hidden"] > td,
    tr[data-repeat-body-lines="hidden"] > th {
      border-top: 0 !important;
      border-bottom: 0 !important;
    }
    tr[data-show-on-last-page] > td,
    tr[data-show-on-last-page] > th {
      box-shadow: inset 0 0 0 2px #28c76f;
    }
    p[data-show-on-first-page] {
      box-shadow: inset 4px 0 0 #4099ff;
    }
    p[data-show-on-last-page] {
      box-shadow: inset 4px 0 0 #28c76f;
    }
    p[data-preserve-space-on-hidden] {
      outline: 1px dashed #ff9f43;
    }
  `

  const isOverflow = fitInfo?.isOverflowWidth || fitInfo?.isOverflowHeight
  const pageRuleBlockOptions = pageRuleBlocks.map((block) => ({
    value: block.index,
    label: block.label,
  }))
  const pageRuleStartOption =
    pageRuleBlockOptions.find(
      (option) => option.value === pageRuleStartIndex,
    ) || null
  const pageRuleEndOption =
    pageRuleBlockOptions.find((option) => option.value === pageRuleEndIndex) ||
    null
  const pageRuleVisibleOption =
    PAGE_RULE_VISIBILITY_OPTIONS.find(
      (option) => option.value === pageRuleVisibleOn,
    ) || PAGE_RULE_VISIBILITY_OPTIONS[0]
  const pageRuleSummary = getTextEditorPageRuleSummary(pageRuleBlocks)
  const selectPortalTarget =
    typeof document !== 'undefined' ? document.body : undefined
  const pageRuleSelectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 2000 }),
  }

  return (
    <div>
      <Modal
        show={showModal}
        dialogClassName="modal-100w"
        enforceFocus={false}
        onHide={() => toggleModal()}
      >
        <Modal.Body style={{ height: 'calc(100vh - 20px)', overflow: 'hidden' }}>
          <div className="d-flex align-items-center justify-content-between mb-1">
            <div className="d-flex align-items-center">
              <Button
                size="sm"
                variant={activeEditorTab === 'content' ? 'primary' : 'outline-primary'}
                className="mr-1"
                title="แก้ไขเนื้อหาและตัวแปรของรายงาน"
                onClick={() => setActiveEditorTab('content')}
              >
                <FeatherIcon size="15" icon="file-text" className="vx-icon" />
                {' '}เนื้อหา
              </Button>
              <Button
                size="sm"
                variant={activeEditorTab === 'style' ? 'primary' : 'outline-primary'}
                title="แก้ไข CSS แยกจากเนื้อหารายงาน"
                onClick={() => setActiveEditorTab('style')}
              >
                <FeatherIcon size="15" icon="code" className="vx-icon" />
                {' '}Style (CSS)
              </Button>
              <Button
                size="sm"
                variant={activeEditorTab === 'page-rule' ? 'primary' : 'outline-primary'}
                className="ml-1"
                title="กำหนดว่าย่อหน้าใดแสดงในหน้าแรกหรือหน้าสุดท้าย"
                onClick={openPageRuleTab}
              >
                <FeatherIcon size="15" icon="layers" className="vx-icon" />
                {' '}การแบ่งหน้า
              </Button>
            </div>
            <Button
              variant="link"
              className="p-0 text-secondary"
              title="ปิดหน้าต่างโดยไม่บันทึก"
              aria-label="ปิดหน้าต่างแก้ไขรายงาน"
              onClick={toggleModal}
            >
              <FeatherIcon size="22" icon="x" className="vx-icon" />
            </Button>
          </div>

          <div style={{ display: activeEditorTab === 'content' ? 'block' : 'none' }}>
            <Editor
              onInit={(evt, editor) => {
                editorRef.current = editor
                applyReportCustomStyle(editor, styleDataDraft)
                window.setTimeout(updateFitInfo, 0)
              }}
              initialValue={sourceCode}
              init={{
              height: editorHeight,
              // menubar: false,
              plugins: [
                'advlist autolink lists link image charmap print preview anchor',
                'searchreplace visualblocks code fullscreen',
                'insertdatetime media table paste code help wordcount',
                'emoticons',
                'importcss',
              ],
              menubar: 'edit view insert format tools table help',
              toolbar:
                'undo redo | bold italic underline strikethrough | ' +
                'fontselect fontsizeselect formatselect | alignleft aligncenter alignright alignjustify | ' +
                'outdent indent |  numlist bullist | forecolor backcolor | pagebreak | ' +
                'reportvariable reportdatasource | table tableprops tablecellprops tablerowprops | ' +
                'tableinsertrowbefore tableinsertrowafter tableinsertcolbefore tableinsertcolafter tabledelete | ' +
                'charmap emoticons insertfile image template link | code fullscreen',
              table_toolbar:
                'reportRepeatRowButton reportRepeatBodyLinesButton reportLastPageOnlyButton reportEqualRowHeightButton reportClearRepeatRowButton | tableprops tablecellprops tablerowprops | ' +
                'tableinsertrowbefore tableinsertrowafter tableinsertcolbefore tableinsertcolafter | ' +
                'tabledelete',
              contextmenu: 'reportVariableFormatCurrency reportVariableFormatNumber0 reportVariableFormatNumber2 reportVariableFormatNone | reportRepeatRow reportRepeatBodyLines reportLastPageOnly reportEqualTableRowHeight reportClearRepeatRow link image table',
              object_resizing: true,
              table_advtab: true,
              table_cell_advtab: true,
              table_row_advtab: true,
              table_resize_bars: true,
              table_use_colgroups: true,
              table_sizing_mode: 'relative',
              extended_valid_elements:
                'tr[class|style|align|valign|data-repeat|data-repeat-source|data-repeat-body-lines|data-repeat-min-rows|data-row-height|data-show-on-first-page|data-show-on-last-page|data-preserve-space-on-hidden],p[data-show-on-first-page|data-show-on-last-page|data-preserve-space-on-hidden],colgroup[span|style],col[span|style|width]',
              fontsize_formats:
                '8px 10px 12px 14px 16px 18px 19px 20px 21px 22px 24px 28px 32px 36px 48px 54px 60px 72px',

              content_style: editorContentStyle,

              font_formats:
                'Arial Black=arial black,avant garde; Courier New=courier new,courier; Lato Black=lato; Roboto=roboto; Sarabun=Sarabun; Angsana=Angsana New; Prompt=Prompt;THSarabunNew=THSarabunNew',
              setup: (editor) => {
                editor.ui.registry.addMenuButton('reportvariable', {
                  text: 'Variable',
                  fetch: (callback) => {
                    callback(getVariableMenuItems(editor))
                  },
                })

                editor.ui.registry.addMenuButton('reportdatasource', {
                  text: 'Repeat',
                  fetch: (callback) => {
                    callback(getRepeatDatasourceMenuItems(editor))
                  },
                })

                editor.ui.registry.addMenuItem('reportVariableFormatCurrency', {
                  text: 'Format: Currency',
                  onAction: () => applyReportVariableFormat(editor, 'currency'),
                  onSetup: (api) => bindVariableFormatEnableState(editor, api),
                })

                editor.ui.registry.addMenuItem('reportVariableFormatNumber0', {
                  text: 'Format: Number 0 decimals',
                  onAction: () => applyReportVariableFormat(editor, 'number', '0'),
                  onSetup: (api) => bindVariableFormatEnableState(editor, api),
                })

                editor.ui.registry.addMenuItem('reportVariableFormatNumber2', {
                  text: 'Format: Number 2 decimals',
                  onAction: () => applyReportVariableFormat(editor, 'number', '2'),
                  onSetup: (api) => bindVariableFormatEnableState(editor, api),
                })

                editor.ui.registry.addMenuItem('reportVariableFormatNone', {
                  text: 'Format: None',
                  onAction: () => applyReportVariableFormat(editor),
                  onSetup: (api) => bindVariableFormatEnableState(editor, api),
                })

                editor.ui.registry.addButton('reportRepeatRowButton', {
                  text: 'Repeat',
                  tooltip: 'Add datasource repeat to current table row',
                  onAction: () => openRepeatDatasourceDialog(editor),
                  onSetup: (api) => bindRepeatEnableState(editor, api),
                })

                editor.ui.registry.addButton('reportClearRepeatRowButton', {
                  text: 'Clear',
                  tooltip: 'Clear datasource repeat from current table row',
                  onAction: () => clearDatasourceRepeat(editor),
                  onSetup: (api) => bindClearRepeatEnableState(editor, api),
                })

                editor.ui.registry.addButton('reportEqualRowHeightButton', {
                  text: 'Equal Height',
                  tooltip: 'Set every row height from the first row in this table',
                  onAction: () => equalizeTableRowHeightFromFirstRow(editor),
                  onSetup: (api) => bindTableRowHeightEnableState(editor, api),
                })

                editor.ui.registry.addToggleButton('reportRepeatBodyLinesButton', {
                  text: 'No Lines',
                  tooltip: 'Hide inner lines in repeat body rows',
                  onAction: () => toggleRepeatBodyLines(editor),
                  onSetup: (api) => bindRepeatBodyLinesState(editor, api),
                })

                editor.ui.registry.addToggleButton('reportLastPageOnlyButton', {
                  text: 'Last Page',
                  tooltip: 'Show current table row only on the last page',
                  onAction: () => toggleLastPageOnly(editor),
                  onSetup: (api) => bindLastPageOnlyState(editor, api),
                })

                editor.ui.registry.addMenuItem('reportRepeatRow', {
                  text: 'Add datasource repeat to row...',
                  onAction: () => openRepeatDatasourceDialog(editor),
                  onSetup: (api) => bindRepeatEnableState(editor, api),
                })

                editor.ui.registry.addMenuItem('reportClearRepeatRow', {
                  text: 'Clear datasource repeat from row',
                  onAction: () => clearDatasourceRepeat(editor),
                  onSetup: (api) => bindClearRepeatEnableState(editor, api),
                })

                editor.ui.registry.addMenuItem('reportEqualTableRowHeight', {
                  text: 'Equal row height from first row',
                  onAction: () => equalizeTableRowHeightFromFirstRow(editor),
                  onSetup: (api) => bindTableRowHeightEnableState(editor, api),
                })

                editor.ui.registry.addToggleMenuItem('reportRepeatBodyLines', {
                  text: 'Hide repeat body lines',
                  onAction: () => toggleRepeatBodyLines(editor),
                  onSetup: (api) => bindRepeatBodyLinesState(editor, api),
                })

                editor.ui.registry.addToggleMenuItem('reportLastPageOnly', {
                  text: 'Show row on last page only',
                  onAction: () => toggleLastPageOnly(editor),
                  onSetup: (api) => bindLastPageOnlyState(editor, api),
                })

                editor.on('contextmenu', (event) => {
                  setEditorSelectionFromPoint(editor, event)
                })

                editor.on('ObjectResized', (event) => {
                  persistResizedTable(editor, event.target)
                  window.setTimeout(updateFitInfo, 0)
                })

                editor.on(
                  'init SetContent NodeChange Change KeyUp ExecCommand',
                  () => window.setTimeout(updateFitInfo, 0),
                )
              },
              }}
            />
            <div
              className={`mt-1 ${isOverflow ? 'text-danger' : 'text-success'}`}
              style={{ fontSize: 13 }}
            >
              {paperGuide.label} {paperGuide.width} x {paperGuide.height}px
              {fitInfo && (
                <>
                  {' '}| Content {fitInfo.width} x {fitInfo.height}px
                  {' '}| {isOverflow ? 'ล้นขอบกระดาษ' : 'พอดีขนาดกระดาษ'}
                </>
              )}
            </div>
          </div>

          <div style={{ display: activeEditorTab === 'style' ? 'block' : 'none' }}>
            <div className="text-muted mb-1" style={{ fontSize: 13 }}>
              เขียน CSS ด้วย class ของรายงาน เช่น .clinical-summary3 โดยไม่ต้องใส่ &lt;style&gt;
              การเปลี่ยนแปลงจะแสดงในแท็บเนื้อหาทันที
            </div>
            <textarea
              className="form-control"
              style={{
                height: editorHeight,
                resize: 'none',
                fontFamily: 'Consolas, Monaco, monospace',
                fontSize: 13,
                lineHeight: 1.5,
              }}
              value={styleDataDraft}
              spellCheck={false}
              aria-label="Report CSS"
              onChange={(event) => setStyleDataDraft(event.target.value)}
            />
          </div>

          <div
            style={{
              display: activeEditorTab === 'page-rule' ? 'block' : 'none',
              height: editorHeight,
              overflowY: 'auto',
            }}
          >
            <div
              className="border rounded p-2 mb-2"
              style={{ backgroundColor: '#f8f9fa' }}
            >
              <div className="font-weight-bold mb-50">
                เลือกว่าข้อไหนต้องอยู่หน้าไหน
              </div>
              <div className="text-muted mb-2" style={{ fontSize: 13 }}>
                ตัวอย่าง: ต้องการให้ข้อ 1–9 อยู่หน้าแรก ให้เลือกตั้งแต่ข้อ 1
                ถึงบรรทัดสุดท้ายก่อนข้อ 10 แล้วเลือก “หน้าแรกเท่านั้น” หากต้องการ
                แก้ของเดิม ให้กด “แก้ไข” ที่ผลลัพธ์ด้านล่าง
              </div>

              <Row>
                <Col md={4}>
                  <FormGroup>
                    <label>1. ข้อแรก</label>
                    <Select
                      value={pageRuleStartOption}
                      options={pageRuleBlockOptions}
                      placeholder="เลือกข้อเริ่มต้น"
                      noOptionsMessage={() => 'ไม่พบย่อหน้าในเนื้อหา'}
                      menuPortalTarget={selectPortalTarget}
                      menuPosition="fixed"
                      styles={pageRuleSelectStyles}
                      onChange={(option) =>
                        setPageRuleStartIndex(option?.value ?? null)
                      }
                    />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <label>2. ข้อหรือบรรทัดสุดท้าย</label>
                    <Select
                      value={pageRuleEndOption}
                      options={pageRuleBlockOptions}
                      placeholder="เลือกข้อหรือบรรทัดสุดท้าย"
                      noOptionsMessage={() => 'ไม่พบย่อหน้าในเนื้อหา'}
                      menuPortalTarget={selectPortalTarget}
                      menuPosition="fixed"
                      styles={pageRuleSelectStyles}
                      onChange={(option) =>
                        setPageRuleEndIndex(option?.value ?? null)
                      }
                    />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <label>3. ต้องการให้อยู่หน้า</label>
                    <Select
                      value={pageRuleVisibleOption}
                      options={PAGE_RULE_VISIBILITY_OPTIONS}
                      menuPortalTarget={selectPortalTarget}
                      menuPosition="fixed"
                      styles={pageRuleSelectStyles}
                      onChange={(option) =>
                        setPageRuleVisibleOn(option?.value || 'all')
                      }
                    />
                  </FormGroup>
                </Col>
              </Row>

              <div className="d-flex align-items-end justify-content-between">
                <details>
                  <summary className="text-muted" style={{ cursor: 'pointer' }}>
                    ตัวเลือกเพิ่มเติม
                  </summary>
                  <Form.Check
                    custom
                    className="mt-50"
                    id="collapse-text-editor-page-rule-space"
                    type="checkbox"
                    label="เมื่อไม่ใช่หน้าที่เลือก ให้เลื่อนข้อถัดไปขึ้น (แนะนำ)"
                    checked={collapsePageRuleSpace}
                    disabled={pageRuleVisibleOn === 'all'}
                    onChange={(event) =>
                      setCollapsePageRuleSpace(event.target.checked)
                    }
                  />
                </details>
                <Button
                  variant="primary"
                  title="บันทึกว่าช่วงเนื้อหานี้ต้องแสดงในหน้าใด"
                  disabled={
                    !pageRuleBlockOptions.length ||
                    pageRuleStartIndex === null ||
                    pageRuleEndIndex === null
                  }
                  onClick={handleApplyPageRule}
                >
                  <FeatherIcon size="16" icon="check" className="vx-icon" />
                  {' '}{pageRuleVisibleOn === 'all'
                    ? 'ให้ช่วงนี้แสดงทุกหน้า'
                    : 'บันทึกการแบ่งหน้า'}
                </Button>
              </div>
            </div>

            <div className="d-flex align-items-center justify-content-between mb-1">
              <div className="font-weight-bold">ผลลัพธ์ที่ตั้งไว้</div>
              <div className="text-muted" style={{ fontSize: 12 }}>
                กด “แก้ไข” เพื่อโหลดช่วงกลับไปด้านบน
              </div>
            </div>

            {!pageRuleSummary.length ? (
              <div className="border rounded p-3 text-center text-muted">
                ยังไม่มีกฎการแบ่งหน้า เนื้อหาทุกย่อหน้าจะแสดงตามปกติ
              </div>
            ) : (
              pageRuleSummary.map((rule, index) => {
                const color = rule.visibleOn === 'first' ? '#4099ff' : '#28c76f'
                const pageLabel =
                  rule.visibleOn === 'first'
                    ? 'หน้าแรกเท่านั้น'
                    : 'หน้าสุดท้ายเท่านั้น'
                const nextBlock = pageRuleBlocks[rule.endIndex + 1]
                const isBlankEnd = rule.endLabel.startsWith('บรรทัดว่าง')
                const endsBeforeNextBlock = isBlankEnd && nextBlock
                const endLabel =
                  endsBeforeNextBlock ? nextBlock.label : rule.endLabel
                const hiddenSpaceLabel = rule.collapseWhenHidden
                  ? 'ซ่อนแล้วเลื่อนข้อด้านล่างขึ้น'
                  : 'ซ่อนแต่คงช่องว่างเดิมไว้'

                return (
                  <div
                    key={`${rule.startIndex}-${rule.endIndex}-${index}`}
                    className="border rounded p-2 mb-1 d-flex align-items-center justify-content-between"
                    style={{ boxShadow: `inset 5px 0 0 ${color}` }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div className="font-weight-bold" style={{ color }}>
                        {pageLabel}
                      </div>
                      <div style={{ fontSize: 13 }}>
                        ตั้งแต่ <strong>{rule.startLabel}</strong>
                        {rule.count > 1 && (
                          <>
                            {' '}{endsBeforeNextBlock
                              ? 'จนถึงบรรทัดก่อน'
                              : 'จนถึง'}{' '}
                            <strong>{endLabel}</strong>
                          </>
                        )}
                      </div>
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        {hiddenSpaceLabel}
                      </div>
                    </div>
                    <div className="text-nowrap ml-2">
                      <Button
                        size="sm"
                        variant="outline-primary"
                        className="mr-1"
                        title="นำช่วงนี้กลับไปแก้ไขในช่องด้านบน"
                        onClick={() => handleEditPageRule(rule)}
                      >
                        <FeatherIcon size="14" icon="edit-2" className="vx-icon" />
                        {' '}แก้ไข
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        title="ลบกฎช่วงนี้และให้เนื้อหากลับมาแสดงทุกหน้า"
                        onClick={() => handleRemovePageRule(rule)}
                      >
                        <FeatherIcon size="14" icon="trash-2" className="vx-icon" />
                        {' '}ลบ
                      </Button>
                    </div>
                  </div>
                )
              })
            )}
          </div>

          <div className="d-flex justify-content-between mt-1">
            <div>
              <input
                ref={wordInputRef}
                type="file"
                accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                className="d-none"
                onChange={handleImportWord}
              />
              {activeEditorTab === 'content' ? (
                <Button
                  outline
                  color="secondary"
                  title="นำเข้าเนื้อหาจากไฟล์ Word (.docx)"
                  onClick={handleImportWordClick}
                  disabled={isImportingWord}
                >
                  <FeatherIcon size="16" icon="upload" className="vx-icon" />
                  {isImportingWord ? ' กำลังแปลง...' : ' นำเข้า Word'}
                </Button>
              ) : activeEditorTab === 'style' ? (
                <Button
                  outline
                  color="secondary"
                  title="คืนค่า CSS เป็นค่าก่อนเปิดหน้าต่างแก้ไข"
                  onClick={() => setStyleDataDraft(styleData || '')}
                >
                  <FeatherIcon size="16" icon="rotate-ccw" className="vx-icon" />
                  {' '}คืนค่า Style
                </Button>
              ) : (
                <Button
                  outline
                  color="secondary"
                  title="ล้างการกำหนดหน้าแรกและหน้าสุดท้ายออกจากทุกย่อหน้า"
                  disabled={!pageRuleSummary.length}
                  onClick={handleClearPageRules}
                >
                  <FeatherIcon size="16" icon="rotate-ccw" className="vx-icon" />
                  {' '}ล้างกฎการแบ่งหน้า
                </Button>
              )}
            </div>
            <Button
              outline
              color="primary"
              title="บันทึกเนื้อหา Style และการแบ่งหน้าของรายงาน"
              onClick={handleSave}
            >
              <FeatherIcon
                      size="16"
                      icon="save"
                      className="vx-icon"            
                    />
               บันทึก
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  )
}

export default TinyEditorModal
